import { useEffect, useState } from "react";
import { apiClient } from "../../../../api/httpClient";

/**
 * Shared /fields + professional guardians + law firm attorneys loading for Bond Retrieval guardian forms.
 * @param {object} options
 * @param {number} [options.attorneyLawFirmId] — passed to `/lawfirm-attorneys/:id`
 * @param {boolean} [options.isGuest] — when true, skip professional-guardian directory fetch
 */
export function useBondRetrievalGuardianDropdowns({
  attorneyLawFirmId = 0,
  mapProfessionalGuardian,
  isGuest = false,
  attorneyFetchMode = "bulk",
}) {
  const [allCities, setAllCities] = useState([]);
  const [allStates, setAllStates] = useState([]);
  const [professionalGuardians, setProfessionalGuardians] = useState([]);
  const [attorneys, setAttorneys] = useState([]);
  const [loadingCitiesStates, setLoadingCitiesStates] = useState(true);
  const [loadingGuardians, setLoadingGuardians] = useState(false);
  const [loadingAttorneys, setLoadingAttorneys] = useState(false);

  useEffect(() => {
    const loadDropdownData = async () => {
      try {
        const cities = await apiClient("/fields/city/", "GET");
        const states = await apiClient("/fields/states/", "GET");
        setAllCities(cities);
        setAllStates(states);
      } catch (error) {
        console.error("Error loading cities or states:", error);
      } finally {
        setLoadingCitiesStates(false);
      }
    };
    loadDropdownData();
  }, []);

  useEffect(() => {
    if (isGuest) {
      setProfessionalGuardians([]);
      setLoadingGuardians(false);
      return undefined;
    }
    let cancelled = false;
    const fetchProfessionalGuardians = async () => {
      setLoadingGuardians(true);
      try {
        const response = await apiClient("/get-all-professional-guardians", "GET");
        const formattedGuardians = response.data.map(mapProfessionalGuardian);
        if (!cancelled) setProfessionalGuardians(formattedGuardians);
      } catch (error) {
        console.error("Error fetching professional guardians:", error);
      } finally {
        if (!cancelled) setLoadingGuardians(false);
      }
    };
    fetchProfessionalGuardians();
    return () => {
      cancelled = true;
    };
  }, [isGuest, mapProfessionalGuardian]);

  useEffect(() => {
    if (attorneyFetchMode === "search") {
      return undefined;
    }
    const fetchAttorneys = async () => {
      setLoadingAttorneys(true);
      try {
        const response = await apiClient(`/lawfirm-attorneys/${attorneyLawFirmId}`, "GET");
        const formattedAttorneys = response.map((attorney) => ({
          value: attorney.lawfirm_user_id,
          label: attorney.full_name,
          position: attorney.position,
          address: attorney.mailing_address,
          city: attorney.city,
          state: attorney.state,
          zip_code: attorney.zip_code,
          email: attorney.email,
          is_gia_on_file: !!attorney.is_gia_on_file,
          is_loa_on_file: !!attorney.is_loa_on_file,
        }));
        setAttorneys(formattedAttorneys);
      } catch (error) {
        console.error("Error fetching attorneys:", error);
      } finally {
        setLoadingAttorneys(false);
      }
    };
    fetchAttorneys();
  }, [attorneyLawFirmId, attorneyFetchMode]);

  return {
    allCities,
    allStates,
    professionalGuardians,
    attorneys,
    loadingCitiesStates,
    loadingGuardians,
    loadingAttorneys,
  };
}
